import React, { useState, useEffect, useCallback } from 'react';

const Contact = ({ t, language, selectedLot }) => {
  const [lots, setLots] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '',
    type: 'typeInvestor', interestedUnit: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const fetchLots = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lots`);
      if (response.ok) {
        const data = await response.json();
        setLots(data.lots || []);
      }
    } catch (error) {
      setLots([
        { id: '01', m2: 950.03, status: 'available' },
        { id: '02', m2: 1234.33, status: 'available' },
        { id: '04', m2: 1304.87, status: 'available' },
        { id: '07', m2: 1531.62, status: 'available' },
        { id: '09', m2: 1362.34, status: 'available' },
        { id: '10', m2: 1362.34, status: 'available' },
        { id: '12', m2: 1294.96, status: 'available' },
        { id: '13', m2: 1077.43, status: 'available' },
        { id: '14', m2: 1232.02, status: 'available' },
        { id: '16', m2: 1232.02, status: 'available' },
        { id: '17', m2: 1232.02, status: 'available' },
        { id: '18', m2: 1232.02, status: 'available' },
        { id: '19', m2: 1382.85, status: 'available' },
        { id: '20', m2: 1225.00, status: 'available' },
        { id: '22', m2: 1225.00, status: 'available' },
        { id: '23', m2: 1225.00, status: 'available' },
        { id: '25', m2: 1396.84, status: 'available' },
        { id: '26', m2: 1215.00, status: 'available' },
        { id: '27', m2: 1215.00, status: 'available' },
        { id: '29', m2: 1215.00, status: 'available' },
        { id: '30', m2: 2545.83, status: 'available' },
        { id: '31', m2: 3455.27, status: 'available' },
      ]);
    }
  }, []);

  useEffect(() => {
    fetchLots();
  }, [fetchLots]);

  useEffect(() => {
    if (selectedLot) {
      setFormData(prev => ({ ...prev, interestedUnit: selectedLot.id }));
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedLot]);

  const availableLots = lots.filter(l => l.status === 'available');

  const formatArea = (m2) => {
    if (language === 'en') {
      return `${(m2 * 1.19599).toLocaleString('en-US', { maximumFractionDigits: 0 })} yd²`;
    }
    return `${m2.toLocaleString(language === 'es' ? 'es-MX' : 'zh-CN', { maximumFractionDigits: 0 })} m²`;
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const selectedLotInfo = formData.interestedUnit ? lots.find(l => l.id === formData.interestedUnit) : null;
      const unitInfo = selectedLotInfo ? `\n\nInterested Unit: Lot ${selectedLotInfo.id} (${formatArea(selectedLotInfo.m2)})` : '';

      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          company: formData.company, interested_unit: formData.interestedUnit || null,
          message: `${t.contact[formData.type]}: ${formData.message}${unitInfo}`,
          language: language,
          selected_lot: formData.interestedUnit || null
        }),
      });

      if (!response.ok) throw new Error('Failed');
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', type: 'typeInvestor', interestedUnit: '', message: '' });
    } catch (error) {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const labels = {
    unit: { en: 'Interested unit', es: 'Unidad de interés', zh: '感兴趣的单元' },
    selectUnit: { en: 'Select unit (optional)', es: 'Seleccionar unidad (opcional)', zh: '选择单元（可选）' }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="container mx-auto px-5 sm:px-6 lg:px-8 max-w-2xl">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-900 mb-8 lg:mb-10 text-center leading-snug">
          {t.contact.title}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">{t.contact.name}</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required
                className="w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">{t.contact.email}</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required
                className="w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">{t.contact.phone}</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                className="w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">{t.contact.company}</label>
              <input type="text" name="company" value={formData.company} onChange={handleChange}
                className="w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">{t.contact.type}</label>
              <select name="type" value={formData.type} onChange={handleChange}
                className="w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent appearance-none cursor-pointer">
                <option value="typeInvestor">{t.contact.typeInvestor}</option>
                <option value="typeTenant">{t.contact.typeTenant}</option>
                <option value="typePartner">{t.contact.typePartner}</option>
                <option value="typeOther">{t.contact.typeOther}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">{labels.unit[language]}</label>
              <select name="interestedUnit" value={formData.interestedUnit} onChange={handleChange}
                className="w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent appearance-none cursor-pointer">
                <option value="">{labels.selectUnit[language]}</option>
                {availableLots.map(lot => (
                  <option key={lot.id} value={lot.id}>Lot {lot.id} — {formatArea(lot.m2)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">{t.contact.message}</label>
            <textarea name="message" value={formData.message} onChange={handleChange} required rows={4}
              className="w-full px-0 py-2.5 border-0 border-b border-gray-300 text-gray-900 text-sm focus:outline-none focus:border-gray-900 bg-transparent resize-none" />
          </div>

          {status === 'success' && (
            <div className="p-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm">{t.contact.success}</div>
          )}
          {status === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{t.contact.error}</div>
          )}

          <button type="submit" disabled={isSubmitting}
            className="w-full sm:w-auto px-8 py-3 bg-gray-900 text-white text-xs uppercase tracking-[0.12em] hover:bg-gray-800 transition-colors disabled:opacity-50">
            {isSubmitting ? '...' : t.contact.submit}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
